/**
 * Компонент выпадающего списка для выбора лицевого счета
 * 
 * Компактный dropdown для выбора между несколькими лицевыми счетами.
 * Показывает номер счета, адрес и баланс для каждого счета.
 * Используется в форме оплаты для авторизованных пользователей с несколькими счетами.
 * Мемоизирован для оптимизации производительности.
 */

import React, { memo, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { formatCurrency } from '../../../utils/formatHelpers';

/**
 * Информация о лицевом счете для отображения в dropdown
 */
export interface AccountDropdownOption {
  /** Уникальный идентификатор счета */
  id: string;
  /** Номер лицевого счета */
  accountNumber: string;
  /** Адрес подключения */
  address?: string;
  /** Текущий баланс */
  balance: number;
  /** Флаг блокировки счета */
  isBlocked?: boolean;
}

/**
 * Свойства компонента AccountSelectorDropdown
 */
export interface AccountSelectorDropdownProps {
  /** Список доступных лицевых счетов */
  accounts: AccountDropdownOption[];
  /** ID выбранного счета */
  selectedAccountId: string;
  /** Обработчик изменения выбранного счета */
  onAccountChange: (accountId: string) => void;
  /** Флаг состояния загрузки */
  disabled?: boolean;
  /** Текст метки */
  label?: string;
}

/**
 * Компонент выпадающего списка для выбора лицевого счета
 * 
 * Отображает компактный dropdown с информацией о каждом счете:
 * - Номер лицевого счета
 * - Адрес подключения
 * - Текущий баланс с цветовой индикацией
 * - Статус блокировки (если заблокирован)
 * 
 * Мемоизирован для предотвращения лишних ре-рендеров.
 * 
 * @param props - Свойства компонента
 * @returns JSX элемент dropdown для выбора счета
 * 
 * @example
 * ```tsx
 * <AccountSelectorDropdown
 *   accounts={accounts}
 *   selectedAccountId={selectedId}
 *   onAccountChange={handleAccountChange}
 * />
 * ```
 */
export const AccountSelectorDropdown: React.FC<AccountSelectorDropdownProps> = memo(({
  accounts,
  selectedAccountId,
  onAccountChange,
  disabled = false,
  label = 'Лицевой счет',
}) => {
  /**
   * Обработчик изменения выбранного счета
   * Мемоизирован для предотвращения создания новой функции при каждом рендере
   * 
   * @param event - Событие изменения Select
   */
  const handleChange = useCallback((event: SelectChangeEvent<string>) => {
    onAccountChange(event.target.value);
  }, [onAccountChange]);

  /**
   * Рендер содержимого выбранного счета
   * Показывает только номер счета для компактности
   * 
   * @param accountId - ID выбранного счета
   * @returns JSX элемент с номером счета
   */
  const renderSelectedValue = (accountId: string) => {
    const account = accounts.find((acc) => acc.id === accountId);
    if (!account) return '';

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <AccountBalanceIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography variant="body1">{account.accountNumber}</Typography>
      </Box>
    );
  };

  /**
   * Рендер элемента списка для каждого счета
   * Показывает полную информацию: номер, адрес, баланс
   * 
   * @param account - Информация о счете
   * @returns JSX элемент MenuItem
   */
  const renderMenuItem = (account: AccountDropdownOption) => {
    return (
      <MenuItem key={account.id} value={account.id}>
        <Box sx={{ width: '100%', py: 0.5 }}>
          {/* Номер счета и баланс */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountBalanceIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2" fontWeight={500}>
                {account.accountNumber}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={0.5}>
              <Chip
                label={formatCurrency(account.balance)}
                color={account.balance >= 0 ? 'success' : 'error'}
                size="small"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
              {account.isBlocked && (
                <Chip
                  label="Заблокирован"
                  color="error"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>

          {/* Адрес */}
          {account.address && (
            <Box display="flex" alignItems="flex-start" gap={0.5} ml={3}>
              <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.2 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {account.address}
              </Typography>
            </Box>
          )}
        </Box>
      </MenuItem>
    );
  };

  // Если счетов нет, не отображаем компонент
  if (accounts.length === 0) {
    return null;
  }

  // Если счет только один, показываем его без dropdown
  if (accounts.length === 1) {
    const account = accounts[0];
    return (
      <Box
        sx={(theme) => ({
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          backgroundColor: 'background.paper',
          
          [theme.breakpoints.down('sm')]: {
            p: 1.5,
          },
        })}
      >
        <Typography 
          variant="caption" 
          color="text.secondary" 
          display="block" 
          mb={1}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              fontSize: '0.7rem',
            },
          })}
        >
          {label}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <AccountBalanceIcon 
            sx={(theme) => ({
              fontSize: 20, 
              color: 'primary.main',
              
              [theme.breakpoints.down('sm')]: {
                fontSize: 18,
              },
            })} 
          />
          <Typography 
            variant="body1" 
            fontWeight={500}
            sx={(theme) => ({
              [theme.breakpoints.down('sm')]: {
                fontSize: '0.9rem',
              },
            })}
          >
            {account.accountNumber}
          </Typography>
          <Chip
            label={formatCurrency(account.balance)}
            color={account.balance >= 0 ? 'success' : 'error'}
            size="small"
            sx={(theme) => ({
              ml: 'auto',
              
              [theme.breakpoints.down('sm')]: {
                height: 18,
                fontSize: '0.7rem',
              },
            })}
          />
        </Box>
        {account.address && (
          <Box display="flex" alignItems="flex-start" gap={0.5}>
            <LocationOnIcon 
              sx={(theme) => ({
                fontSize: 16, 
                color: 'text.secondary', 
                mt: 0.2,
                
                [theme.breakpoints.down('sm')]: {
                  fontSize: 14,
                },
              })} 
            />
            <Typography 
              variant="body2" 
              color="text.secondary" 
              fontSize="0.875rem"
              sx={(theme) => ({
                [theme.breakpoints.down('sm')]: {
                  fontSize: '0.8rem',
                },
              })}
            >
              {account.address}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Если счетов несколько, показываем dropdown
  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel 
        id="account-selector-label"
        sx={(theme) => ({
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.9rem',
          },
        })}
      >
        {label}
      </InputLabel>
      <Select
        labelId="account-selector-label"
        id="account-selector"
        value={selectedAccountId}
        label={label}
        onChange={handleChange}
        renderValue={renderSelectedValue}
        sx={(theme) => ({
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
          },
          
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.9rem',
          },
        })}
        MenuProps={{
          PaperProps: {
            sx: (theme) => ({
              maxHeight: 400,
              '& .MuiMenuItem-root': {
                whiteSpace: 'normal',
                py: 1.5,
              },
              
              [theme.breakpoints.down('sm')]: {
                maxHeight: 300,
                '& .MuiMenuItem-root': {
                  py: 1,
                  fontSize: '0.85rem',
                },
              },
            }),
          },
        }}
      >
        {accounts.map(renderMenuItem)}
      </Select>
    </FormControl>
  );
});

AccountSelectorDropdown.displayName = 'AccountSelectorDropdown';
